import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';

import { Box, Button, Card, CardContent, Divider, Typography, useTheme } from '@mui/material';
import { ChildCare, Person } from '@mui/icons-material';
import { PaxResponseDto } from '@bandido/shared/dtos';

import { fromYYYYMMDDToDDMMYYYY, getExactAgeForDOB } from '../../../common/helper';
import { ListLayout } from '../../../common/components/ListLayout';
import { useGetSaleOrderPaxes } from '../../queries';
import { HotelPaxForm } from '../hotel-pax-form';

export const SaleOrderPaxes:FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const theme = useTheme();
  const { data } = useGetSaleOrderPaxes({ id });
  const [selectedPax, setSelectedPax] = useState<PaxResponseDto | null>(null);
  const [hotelPaxFormOpen, setHotelPaxFormOpen] = useState(false);

  const handleOpenHotelPaxForm = (pax: PaxResponseDto) => {
    setSelectedPax({
      id: pax.id,
      name: pax.name,
      lastName: pax.lastName,
      dob: pax.dob,
      email: pax.email || '',
      phone: pax.phone || '',
    });
    setHotelPaxFormOpen(true);
  };

  const handleCloseHotelPaxForm = () => {
    setSelectedPax(null);
    setHotelPaxFormOpen(false);
  };

  const handleAddPax = () => {
    setSelectedPax({
      id: uuid(),
      name: '',
      lastName: '',
      dob: '',
      email: '',
    });
    setHotelPaxFormOpen(true);
  };

  if(!id) {
    return null;
  }

  return (
    <ListLayout
      padding={0}
      title={t('saleOrder.view.passengers.title')}
      subtitle={t('saleOrder.view.passengers.subtitle')}
    >
      <Box display="flex" justifyContent="flex-end" mb={4}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleAddPax}
        >
          {t('saleOrder.view.passengers.newPassenger')}
        </Button>
      </Box>
      <Card>
        <CardContent 
          sx={{
            background: theme.palette.grey[100]
          }}
        >
          <Box
            display="table"
            width="100%"
            my={2}
          >
            <Box
              display="table-row"
              fontWeight="bold" 
              padding={2}
            >
              <Box display="table-cell" textAlign="left" width="10%" maxWidth={140} px={1}>
                <Typography variant="body2">{t('saleOrder.view.passengers.table.name')}</Typography>
              </Box>
              <Box display="table-cell" textAlign="left" width="20%" maxWidth={140} px={1}>
                <Typography variant="body2">{t('saleOrder.view.passengers.table.lastname')}</Typography>
              </Box>
              <Box display="table-cell" textAlign="left" width="20%" maxWidth={200} px={1}>
                <Typography variant="body2">{t('saleOrder.view.passengers.table.email')}</Typography>
              </Box>
              <Box display="table-cell" textAlign="right" width="20%" maxWidth={100} px={1}>
                <Typography variant="body2">{t('saleOrder.view.passengers.table.age')}</Typography>
              </Box>
              <Box display="table-cell" textAlign="right" width="20%" maxWidth={100} px={1}>
                <Typography variant="body2">{t('saleOrder.view.passengers.table.dob')}</Typography>
              </Box>
              <Box display="table-cell" textAlign="right" width="10%" maxWidth={100} px={1}>
                <Typography variant="body2">{t('saleOrder.view.passengers.table.actions')}</Typography>
              </Box>
            </Box>
          </Box>
          <Divider />
          <Box display="table" width="100%" my={2}>
            {data?.map((pax, idx) => {
              const { dob, name, lastName, email } = pax;
              const formattedDOB = fromYYYYMMDDToDDMMYYYY(dob);
              const age = getExactAgeForDOB(dob);
              return (
                <Box
                  key={`${idx}-${name}-${lastName}`}
                  display="table-row"
                  my={1}
                >
                  <Box display="table-cell" width="10%" maxWidth={140} p={1} sx={{ verticalAlign: 'middle' }}>
                    <Box display="flex" alignItems="center" gap={1}>
                      {age >= 18 ? (
                        <Person sx={{ color: theme.palette.action.disabled }} />
                      ) : (
                        <ChildCare sx={{ color: theme.palette.action.disabled }} />
                      )}
                      <Typography variant="subtitle2" noWrap>{name}</Typography>
                    </Box>
                  </Box>

                  <Box display="table-cell" width="20%" maxWidth={140} p={1} sx={{ verticalAlign: 'middle' }}>
                    <Typography variant="subtitle2" noWrap>{lastName}</Typography>
                  </Box>

                  <Box display="table-cell" width="20%" maxWidth={200} p={1} sx={{ verticalAlign: 'middle' }}>
                    <Typography variant="subtitle2" noWrap>{email || t('common.notValueFound')}</Typography>
                  </Box>

                  <Box display="table-cell" width="20%" maxWidth={100} p={1} sx={{ verticalAlign: 'middle' }}>
                    <Typography variant="subtitle2" align="right">{age}</Typography>
                  </Box>

                  <Box display="table-cell" width="20%" maxWidth={100} p={1} sx={{ verticalAlign: 'middle' }}>
                    <Typography variant="subtitle2" align="right">{formattedDOB}</Typography>
                  </Box>
                  <Box display="table-cell" width="10%" maxWidth={100} p={1} sx={{ verticalAlign: 'middle', textAlign: 'right' }}>
                    <Button
                      variant="text"
                      color="primary"
                      onClick={() => handleOpenHotelPaxForm(pax)}
                      size="small"
                    >
                      {t('common.update')}
                    </Button>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Card>

      <HotelPaxForm
        isOpen={hotelPaxFormOpen}
        pax={selectedPax}
        saleOrderId={id}
        onBack={handleCloseHotelPaxForm}
        onContinue={handleCloseHotelPaxForm}
      />
    </ListLayout>
  );
};
